import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import './Toast.css';

const VISIBLE_MS = 8000;

interface ToastRequest {
  message: string;
  /** Omit for a plain confirmation with nothing to take back. */
  onUndo?: () => void | Promise<void>;
}

interface Toast extends ToastRequest {
  id: number;
}

const ToastContext = createContext<(toast: ToastRequest) => void>(() => {});

/**
 * "Done. Undo?"
 *
 * The alternative to a confirmation dialog on every small action. A dialog
 * asks a tutor to predict what they want before they can see the result; a
 * toast lets them do the thing, look at it, and take it back.
 *
 * It is deliberately not used for the actions that are genuinely
 * irreversible - deleting a student takes their whole lesson history with
 * them - because offering an undo that cannot honour itself is worse than
 * offering nothing.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { tr } = useTranslation();
  const [toast, setToast] = useState<Toast | null>(null);
  const nextId = useRef(1);

  // One at a time. A stack of toasts covers the thing the tutor just acted on,
  // which is the one part of the screen they are looking at.
  const show = useCallback((request: ToastRequest) => {
    setToast({ ...request, id: nextId.current++ });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <div className="toast" role="status" aria-live="polite">
          <span className="toast-message">{toast.message}</span>
          {toast.onUndo && (
            <button
              type="button"
              className="toast-undo"
              onClick={() => {
                // Dismissed first: undoing is the end of this toast whether
                // or not the call behind it succeeds.
                setToast(null);
                void toast.onUndo?.();
              }}
            >
              {tr('undo')}
            </button>
          )}
          <button type="button" className="toast-close" onClick={() => setToast(null)} aria-label={tr('close')}>
            ×
          </button>
        </div>
      )}
    </ToastContext.Provider>
  );
}

/** Returns a function that shows one toast. Safe to call outside a provider. */
export function useToast() {
  return useContext(ToastContext);
}
